import { fetchDataWithFetch } from "./fetchBase";
import type { ThemePreference, ThemeSchedule } from '../types/ThemeType';

type CreateInvitationParams = {
  folderId: string;
  inviteeId: string;
  role: 'admin' | 'editor' | 'viewer';
  hiddenResources?: Array<{
    resourceType: 'folder' | 'dataset' | 'board' | 'workflow';
    resourceId: string;
  }>;
};

export type GetIncomingInvitationsResponse = {
  id: string;
  folder: {
    id: string;
    name: string;
  };
  inviter: {
    id: string;
    name: string;
    email: string;
  };
  role: "admin" | "editor" | "viewer";
  createdAt: string;
  expiresAt: string;
  daysRemaining: number;
};

export type GetSentInvitationsResponse = {
  id: string;
  folder: {
    id: string;
    name: string;
  };
  invitee: {
    id: string;
    name: string;
    email: string;
  };
  role: "admin" | "editor" | "viewer";
  createdAt: string;
  expiresAt:string;
  daysRemaining: number;
};

type CreateInvitationResponse = {
  _id: string;
  folderId: {
    _id: string;
    name: string;
  };
  inviterId: {
    _id: string;
    name: string;
    email: string;
  };
  inviteeId: {
    _id: string;
    name: string;
    email: string;
  };
  role: "admin" | "editor" | "viewer";
  createdAt: string;
  expiresAt: string;
};

type AcceptInvitationResponse = {
  message: string;
  folder: {
    id: string;
    name: string;
  };
  role: "admin" | "editor" | "viewer";
  settings: {
    mode: ThemePreference;
    currentLang: string;
    accent: 'brick' | 'sakura' | 'emerald' | 'royal';
    isInitialized: boolean;
    themeSchedule?: ThemeSchedule;
  };
};

type SearchUsersForInvitationParams = {
  searchTerm: string;
};

type SearchUsersForInvitationResponse = {
  id: string;
  name: string;
  email: string;
};

export async function apiGetIncomingInvitations(): Promise<GetIncomingInvitationsResponse[]> {
  return fetchDataWithFetch<GetIncomingInvitationsResponse[]>('/invitations/incoming', {
    method: 'GET'
  });
}

export async function apiGetSentInvitations(): Promise<GetSentInvitationsResponse[]> {
  return fetchDataWithFetch<GetSentInvitationsResponse[]>('/invitations/sent', {
    method: 'GET'
  });
}

export async function apiCreateInvitation(data: CreateInvitationParams): Promise<CreateInvitationResponse> {
  return fetchDataWithFetch<CreateInvitationResponse>('/invitations', {
    method: 'POST',
    data,
  });
}

export async function apiAcceptInvitation(id: string): Promise<AcceptInvitationResponse> {
  return fetchDataWithFetch<AcceptInvitationResponse>(`/invitations/${id}/accept`, {
    method: 'POST',
  });
}

export async function apiRejectInvitation(id: string): Promise<{ message: string }> {
  return fetchDataWithFetch(`/invitations/${id}/reject`, {
    method: 'DELETE',
  });
}

export async function apiCancelInvitation(id: string): Promise<{ message: string }> {
  return fetchDataWithFetch(`/invitations/${id}/cancel`, {
    method: 'DELETE',
  });
}

export async function apiSearchUsersForInvitation({ 
  searchTerm 
}: SearchUsersForInvitationParams): Promise<SearchUsersForInvitationResponse> {
  const queryParams = new URLSearchParams({ q: searchTerm });
  const url = `/users/search?${queryParams}`;
 
  return fetchDataWithFetch<SearchUsersForInvitationResponse>(url, {
    method: 'GET'
  });
}